import React from "react";
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { EdgeDevice } from "./edgeDeviceInterfaces";
import {
  getEdgeDeviceWireguardTunnelLabel,
} from "./edgeDeviceFilterUtils";
import { findMatchingWireguardTunnel } from "./edgeDeviceTunnelLinkUtils";
import { WireguardTunnelNameLink } from "./WireguardTunnelNameLink";
import { WireGuardTunnel } from "./wireGuardTunnelsInterfaces";

type SortDirection = "asc" | "desc";
const TABLE_MIN_WIDTH = 1050;

type SortKey =
  | "serial_number"
  | "mac_address"
  | "manufacturer"
  | "sku"
  | "wireguard_tunnel"
  | "provisioning_state";

const headCells: Array<{ id: SortKey; label: string }> = [
  { id: "serial_number", label: "Serial Number" },
  { id: "mac_address", label: "Mac Address" },
  { id: "manufacturer", label: "Manufacturer" },
  { id: "sku", label: "Sku" },
  { id: "wireguard_tunnel", label: "Wireguard Tunnel" },
  { id: "provisioning_state", label: "Provisioning State" },
];

interface Props {
  rows: EdgeDevice[];
  searchText: string;
  onSearchTextChange: (value: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (_: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddEdgeDeviceClick: () => void;
  onRowClick: (row: EdgeDevice) => void;
  wireguardTunnels: WireGuardTunnel[];
  onOpenWireguardTunnel: (tunnel: WireGuardTunnel) => void;
}

const getSortValue = (row: EdgeDevice, sortKey: SortKey): string => {
  if (sortKey === "wireguard_tunnel") {
    return getEdgeDeviceWireguardTunnelLabel(row);
  }
  return String(row[sortKey] ?? "");
};

export const EdgeDevicesTable = ({
  rows,
  searchText,
  onSearchTextChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onAddEdgeDeviceClick,
  onRowClick,
  wireguardTunnels,
  onOpenWireguardTunnel,
}: Props) => {
  const [orderBy, setOrderBy] = React.useState<SortKey>("serial_number");
  const [order, setOrder] = React.useState<SortDirection>("asc");

  const sortedRows = React.useMemo(() => {
    const sorted = [...rows];
    sorted.sort((first, second) => {
      const firstValue = getSortValue(first, orderBy);
      const secondValue = getSortValue(second, orderBy);
      const compareValue = firstValue.localeCompare(secondValue);
      return order === "asc" ? compareValue : -compareValue;
    });
    return sorted;
  }, [order, orderBy, rows]);

  const visibleRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const requestSort = (property: SortKey) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Paper
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          className="relative"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 4,
            background: "white",
          }}
        >
          <div className="px-4 pt-[10px] pb-[10px]">
            <div className="flex items-center gap-3">
              <TextField
                size="small"
                placeholder="Filter by serial number, MAC address, manufacturer, tunnel..."
                value={searchText}
                onChange={(event) => onSearchTextChange(event.target.value)}
                sx={{
                  width: "33%",
                  minWidth: 340,
                  maxWidth: 520,
                  flexShrink: 0,
                  backgroundColor: "#e9edf2",
                  "& *": { fontFamily: "Mulish" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                <Button
                  variant="contained"
                  onClick={onAddEdgeDeviceClick}
                  sx={{
                    fontFamily: "Mulish",
                    textTransform: "none",
                    backgroundColor: "#FF8400",
                    "&:hover": { backgroundColor: "#E67300" },
                  }}
                >
                  + GENERATE PROVISION TOKEN
                </Button>
              </div>
            </div>
          </div>
        </div>
        <Table
          sx={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: TABLE_MIN_WIDTH,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <TableHead
            sx={{
              display: "table",
              width: "100%",
              minWidth: TABLE_MIN_WIDTH,
              tableLayout: "fixed",
            }}
          >
            <TableRow>
              {headCells.map((cell) => (
                <TableCell
                  key={cell.id}
                  sx={{ fontFamily: "Mulish", fontWeight: 700 }}
                >
                  <TableSortLabel
                    active={orderBy === cell.id}
                    direction={orderBy === cell.id ? order : "asc"}
                    onClick={() => requestSort(cell.id)}
                  >
                    <span style={{ fontFamily: "Mulish" }}>{cell.label}</span>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              display: visibleRows.length === 0 ? "flex" : "block",
              alignItems: visibleRows.length === 0 ? "center" : undefined,
              justifyContent: visibleRows.length === 0 ? "center" : undefined,
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
              width: "100%",
              minWidth: TABLE_MIN_WIDTH,
            }}
          >
            {visibleRows.length === 0 ? (
              <Box
                sx={{
                  fontFamily: "Mulish",
                  color: "text.secondary",
                  px: 2,
                  textAlign: "center",
                }}
              >
                No edge devices match the current search.
              </Box>
            ) : (
              visibleRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => onRowClick(row)}
                  sx={{
                    display: "table",
                    tableLayout: "fixed",
                    width: "100%",
                    minWidth: TABLE_MIN_WIDTH,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#e3ecfa !important",
                      "& .MuiTableCell-root": {
                        backgroundColor: "#e3ecfa !important",
                      },
                    },
                  }}
                >
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {row.serial_number || "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {row.mac_address || "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {row.manufacturer || "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>{row.sku || "-"}</TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {row.tunnel?.tunnel_name || "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {row.provisioning_state || "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={sortedRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{ borderTop: "1px solid #d1d5db" }}
      />
    </Paper>
  );
};
